import React, { useState, useCallback } from 'react';
import { styled, Theme } from '@mui/material/styles';
import { Box, Button, Divider } from '@mui/material';
import { DigitalQuestion, QuestionTypeV2 } from '../../redux/types';
import MultiChoiceQuestion from '../questions/multiChoice/MultiChoiceQuestion';

const PREFIX = 'DigitalContextQuestionEditor';

const classes = {
  margin: `${PREFIX}-margin`,
};

const StyledBox = styled(Box)((
  { theme }: { theme: Theme },
) => ({
  [`& .${classes.margin}`]: {
    margin: theme.spacing(2),
  },
}));

type Props = {
  surveyId: number;
  digitalQuestions: DigitalQuestion[];
  addDigitalQuestion: (dq: Omit<DigitalQuestion, 'id'>) => void
  updateDigitalQuestion: (dq: DigitalQuestion) => void
  deleteDigitalQuestion: (qId: number) => void;
}

const DigitalContextQuestionEditor: React.FC<Props> = ({
  surveyId,
  digitalQuestions,
  addDigitalQuestion: addDigitalQuestionAction,
  updateDigitalQuestion: updateDigitalQuestionAction,
  deleteDigitalQuestion,
}: Props) => {
  const [editBuffer, setEditBUffer] = useState<DigitalQuestion>();

  const contextQuestions = digitalQuestions.filter((q) => q.isContextQuestion);

  const onEditCancel = useCallback(() => {
    setEditBUffer(undefined);
  }, []);

  const addDigitalQuestion = useCallback((dq: Omit<DigitalQuestion, 'id'>): void => {
    addDigitalQuestionAction({
      ...dq,
      isContextQuestion: true,
    });
  }, [addDigitalQuestionAction]);

  const updateDigitalQuestion = useCallback((dq: DigitalQuestion): void => {
    updateDigitalQuestionAction({
      ...dq,
      isContextQuestion: true,
    });
  }, [updateDigitalQuestionAction]);

  return (
    <StyledBox>
      {
        contextQuestions.map((cq) => (
          <React.Fragment key={cq.id}>
            <Box className={classes.margin} id="context-question-container">
              {
                (!editBuffer || editBuffer.id !== cq.id) && cq.type === 'MultiChoice' && (
                  <Box display="flex" alignItems="flex-start">
                    <Box flexGrow={1}>
                      <MultiChoiceQuestion mode="view" question={cq} />
                    </Box>
                    <Button color="tertiary" onClick={() => setEditBUffer(cq)}>Edit</Button>
                    <Button color="tertiary" onClick={() => deleteDigitalQuestion(cq.id)}>Delete</Button>
                  </Box>
                )
              }
              {
                editBuffer && editBuffer.id === cq.id && cq.type === QuestionTypeV2.MultiChoice && (
                  <MultiChoiceQuestion
                    mode="edit"
                    surveyId={surveyId}
                    question={cq}
                    updateQuestion={updateDigitalQuestion}
                    onCancelEdit={onEditCancel}
                    disableHideAggregation
                  />
                )
              }
            </Box>
            <Divider />
          </React.Fragment>
        ))
      }
      {
        !editBuffer && (
          <Box className="mt3">
            <MultiChoiceQuestion
              mode="edit"
              surveyId={surveyId}
              addQuestion={addDigitalQuestion}
              disableHideAggregation
            />
          </Box>
        )
      }
    </StyledBox>
  );
};

export default DigitalContextQuestionEditor;
